import { ActionIcon, Box, Group, Modal, ScrollArea, Space, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBuilding, IconMessageShare, IconX } from '@tabler/icons-react';
import { FeedbackForm } from 'components/FeedbackForm';
import { Logo } from 'components/Logo/Logo';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import UserMenuButton from 'modules/Navigation/components/UserMenuButton';
import { Link } from 'react-router-dom';
import { GROUP_ROUTE } from 'shared/constants';
import { MenuItemBadges } from './components/MenuItemBadges';
import { MobileMenuButton } from './components/MobileMenuButton';
import { NavMenuItem } from './components/NavMenuItem';
import { useBuildGroupMenuItems } from './hooks/use-build-group-menu-items';

interface GroupNavBarProps {
  closeNavbarOnMobile: () => void;
  onClickOpenNavbar: () => void;
}

export const GroupNavBar = ({ closeNavbarOnMobile, onClickOpenNavbar }: GroupNavBarProps) => {
  const { t } = useTranslation();
  const groupMenuItems = useBuildGroupMenuItems();
  const { group } = useProfile();
  const [isFeedbackOpen, { open: openFeedback, close: closeFeedback }] = useDisclosure(false);

  return (
    <Stack bg="purple.0" py="space6" px="space3" h="100%" gap={0} pos="relative">
      <MobileMenuButton onClickOpenNavbar={onClickOpenNavbar} />
      <Group ml="space1" mb="space5">
        <Link to={GROUP_ROUTE}>
          <Logo variant="fullLogo" height={32} />
        </Link>
        <ActionIcon
          hiddenFrom="mobile"
          color="gray.6"
          variant="transparent"
          aria-label={t(i18nKeys.CLOSE)}
          onClick={closeNavbarOnMobile}
        >
          <IconX />
        </ActionIcon>
      </Group>
      <ScrollArea flex={1}>
        <Box component="nav" data-tour="group-nav">
          {groupMenuItems.map(item => (
            <NavMenuItem key={item.label} {...item} onNavigate={closeNavbarOnMobile} />
          ))}
        </Box>
      </ScrollArea>
      <Box data-tour="group-feedback">
        <NavMenuItem
          label={t(i18nKeys.FEEDBACK.BUTTON)}
          Icon={IconMessageShare}
          onClick={openFeedback}
        />
      </Box>
      <Modal
        opened={isFeedbackOpen}
        onClose={closeFeedback}
        centered
        title={
          <Group gap="xs">
            {t(i18nKeys.GROUP.FEEDBACK.TITLE)}
            <MenuItemBadges tagLabel="beta" />
          </Group>
        }
      >
        <FeedbackForm
          featureName="Group"
          isFillingHeight={false}
          onSent={closeFeedback}
          description={t(i18nKeys.GROUP.FEEDBACK.DESCRIPTION)}
        />
      </Modal>
      {group != null && (
        <Group gap={6} mt="sm" px="sm" py="xs" bg="purple.1" style={{ borderRadius: 10 }}>
          <IconBuilding size={15} color="var(--mantine-color-purple-6)" />
          <Text size="xs" c="purple.6">
            <Text span inherit fw={600}>
              {t(i18nKeys.GROUP.VIEW)}
            </Text>
            {` · ${t(i18nKeys.GROUP.COMPANY_COUNT, { count: group.companiesCount })}`}
          </Text>
        </Group>
      )}
      <Space h="space6" />
      <UserMenuButton />
    </Stack>
  );
};
